import { GridRowSelectionModel } from '@mui/x-data-grid';
import { isString } from 'lodash';
import { makeApi } from 'src/http/api/internal/internal-http';
import HomeBannersRepository from 'src/repositories/personalization/home-banners/home-banners.repository';
import { homeBannerItem } from 'src/types/home-banners';
import { toBase64 } from 'src/utils/utils';
import { create } from 'zustand';

type States = {
  homeBanners: homeBannerItem[];
  homeBannerToEdit: homeBannerItem | null;
  isLoading: boolean;
  isLoadingCreate: boolean;
  isLoadingDetails: boolean;
  selectedBanners: GridRowSelectionModel;
};

type Actions = {
  findAll: () => Promise<void>;
  findOne: (id: string) => Promise<void>;
  resetHomeBannerDetails: () => void;
  update: (id: string, homeBand: homeBannerItem) => Promise<boolean>;
  create: (homeBand: homeBannerItem) => Promise<boolean>;
  changeHomeBannerStatus: (bannerId: string, newStatus: 'active' | 'inactive') => Promise<void>;
  setSelectedBanners: (banners: GridRowSelectionModel) => void;
  deleteSelectedBanners: () => Promise<void>;
};

type Store = Actions & States;

const initialValues = {
  homeBanners: [],
  isLoading: false,
  isLoadingCreate: false,
  isLoadingDetails: false,
  homeBannerToEdit: null,
  selectedBanners: [],
};

const repository = new HomeBannersRepository(makeApi());

export const useHomeBannersStore = create<Store>((set) => ({
  ...initialValues,

  findAll: async () => {
    set({ homeBannerToEdit: null, isLoading: true });
    const response = await repository.findAllBanners();
    if (response.isLeft()) {
      set({ isLoading: false });
      return;
    }

    set({ homeBanners: response.value, isLoading: false });
  },

  findOne: async (id: string) => {
    set({ isLoadingDetails: true });
    const response = await repository.findOne(id);
    if (response.isLeft()) {
      set({ isLoadingDetails: false });
      return;
    }
    set({ homeBannerToEdit: response.value, isLoadingDetails: false });
  },

  resetHomeBannerDetails: () => {
    set({ homeBannerToEdit: null });
  },

  update: async (id: string, homeBanner: homeBannerItem) => {
    set({ isLoadingCreate: true });
    const map = homeBanner.images.map(async (image, idx) => {
      if (!isString(image.url)) {
        try {
          const imgBase64: any = await toBase64(image.url);
          const res: any = await repository.uploadFile({
            name: image.url?.name,
            base64: imgBase64,
          });
          homeBanner.images[idx].url = res.value.data.url;
        } catch (error) {
          console.error(error);
        }
      }
    })
    await Promise.all(map)
    const response = await repository.update(id, homeBanner);
    if (response.isLeft()) {
      set({ isLoadingCreate: false });
      return false;
    }
    set({ isLoadingCreate: false });
    return true;
  },

  create: async (homeBanner: homeBannerItem) => {
    set({ isLoadingCreate: true });
    const map = homeBanner.images.map(async (image, idx) => {
      if (!isString(image.url)) {
        try {
          const imgBase64: any = await toBase64(image.url);
          const res: any = await repository.uploadFile({
            name: image.url?.name,
            base64: imgBase64,
          });
          homeBanner.images[idx].url = res.value.data.url;
        } catch (error) {
          console.error(error);
        }
      }
    })
    await Promise.all(map)
    const response = await repository.create(homeBanner);
    if (response.isLeft()) {
      set({ isLoadingCreate: false });
      return false;
    }
    set({ isLoadingCreate: false });
    return true;
  },

  changeHomeBannerStatus: async (bannerId: string, newStatus: 'active' | 'inactive') => {
    const response = await repository.changeHomeBannerStatus(bannerId, newStatus);
    if (response.isLeft()) {
      throw new Error(response.value.message);
    }
  },

  setSelectedBanners: (banners: GridRowSelectionModel) => {
    set({ selectedBanners: banners });
  },

  deleteSelectedBanners: async () => {
    const { selectedBanners } = useHomeBannersStore.getState();
    const response = await repository.deleteBanners(selectedBanners as string[]);
    if (response.isLeft()) {
      throw new Error(response.value.message);
    }
  },
}));
